import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { APP_LAUNCHER_EAGLEVIEW } from '@rsos/constants/appLauncherTypes';
import {
  setSelectedCallerID,
  viewAllCalls,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import { setCoordinates } from '../geoSelection/geoSelectionSlice';
import { closeAllCards } from '../mapCard/mapCardSlice';
import type {
  AppLauncherState,
  SelectedAppLauncherApp,
} from './appLauncher.types';
import { getAppLauncherApps, postDeeplinkURL } from './appLauncherAPI';
import type { PostDeeplinkURLData } from './appLauncherAPI';

export const initialState: AppLauncherState = {
  applications: [],
  selectedAppLauncherApp: null,
  error: {
    fetchAppLauncherApps: null,
    createDeeplinkURL: null,
  },
  loading: {
    fetchAppLauncherApps: false,
    createDeeplinkURL: false,
  },
};

export const fetchAppLauncherApps = createAsyncThunk(
  'appLauncher/fetchAppLauncherApps',
  async (orgName: string, { rejectWithValue }) => {
    try {
      const response = await getAppLauncherApps(orgName);
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const createDeeplinkURL = createAsyncThunk(
  'appLauncher/createDeeplinkURL',
  async (data: PostDeeplinkURLData, { rejectWithValue }) => {
    try {
      const response = await postDeeplinkURL(data);
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const appLauncherSlice = createSlice({
  name: 'appLauncher',
  initialState,
  reducers: {
    selectAppLauncherApp: (
      state,
      action: { payload: { appName?: string; phone?: string } | null },
    ) => {
      const { payload } = action;
      if (!payload) {
        state.selectedAppLauncherApp = initialState.selectedAppLauncherApp;
        return;
      }
      const { appName = '', phone = '' } = payload;
      state.selectedAppLauncherApp = {
        appName,
        phone,
      } as SelectedAppLauncherApp;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAppLauncherApps.pending, state => {
        state.loading.fetchAppLauncherApps = true;
      })
      .addCase(fetchAppLauncherApps.fulfilled, (state, action) => {
        state.error.fetchAppLauncherApps = null;
        state.loading.fetchAppLauncherApps = false;
        state.applications = action.payload.applications ?? [];
      })
      .addCase(fetchAppLauncherApps.rejected, (state, action) => {
        state.error.fetchAppLauncherApps = (action.payload as string) ?? null;
        state.loading.fetchAppLauncherApps = false;
      })
      .addCase(createDeeplinkURL.pending, state => {
        state.loading.createDeeplinkURL = true;
      })
      .addCase(createDeeplinkURL.fulfilled, state => {
        state.error.createDeeplinkURL = null;
        state.loading.createDeeplinkURL = false;
      })
      .addCase(createDeeplinkURL.rejected, (state, action) => {
        if (action.payload) {
          state.error.createDeeplinkURL = action.payload as string;
          state.loading.createDeeplinkURL = false;
        }
      })
      .addCase(viewAllCalls, state => {
        if (state.selectedAppLauncherApp?.appName !== APP_LAUNCHER_EAGLEVIEW) {
          return { ...state, selectedAppLauncherApp: null };
        }
      })
      .addCase(setSelectedCallerID, state => {
        if (state.selectedAppLauncherApp?.appName !== APP_LAUNCHER_EAGLEVIEW) {
          return { ...state, selectedAppLauncherApp: null };
        }
      })
      .addCase(closeAllCards, state => {
        if (state.selectedAppLauncherApp?.appName !== APP_LAUNCHER_EAGLEVIEW) {
          state.selectedAppLauncherApp = initialState.selectedAppLauncherApp;
        }
      })
      .addCase(setCoordinates, state => {
        state.selectedAppLauncherApp = initialState.selectedAppLauncherApp;
      });
  },
});

export const { selectAppLauncherApp } = appLauncherSlice.actions;
export default appLauncherSlice.reducer;
